import { Head, usePage } from '@inertiajs/react';

// Helper to attach Inertia's head-key attribute without TypeScript complaints
function hk(key: string): Record<string, string> {
    return { 'head-key': key };
}

interface PageSeoProps {
    title: string;
    description: string;
    /** URL path, e.g. "/" or "/terms" */
    path?: string;
    /** Absolute image URL or root-relative path. Defaults to /og-image.png */
    image?: string;
    type?: 'website' | 'article';
    /** Pass true for authenticated/private pages that should not be indexed */
    noIndex?: boolean;
    /** Primary locale. Defaults to ar_SA */
    locale?: 'ar_SA' | 'en_US';
}

const SITE_NAME = 'Mujarrad Studio';

export default function PageSeo({
    title,
    description,
    path = '/',
    image = '/og.jpg',
    type = 'website',
    noIndex = false,
    locale = 'ar_SA',
}: PageSeoProps) {
    const appUrl = ((usePage().props.appUrl ?? '') as string).replace(/\/$/, '');
    const canonicalUrl = `${appUrl}${path === '/' ? '/' : `${path}/`}`;
    const imageUrl = image.startsWith('http') ? image : `${appUrl}${image}`;
    const altLocale = locale === 'ar_SA' ? 'en_US' : 'ar_SA';

    return (
        <Head>
            <title>{title}</title>

            {noIndex
                ? <meta {...hk('robots')} name="robots" content="noindex, nofollow" />
                : <meta {...hk('robots')} name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
            }

            <meta {...hk('description')} name="description" content={description} />
            <link {...hk('canonical')} rel="canonical" href={canonicalUrl} />
            <link {...hk('alternate-ar')} rel="alternate" hrefLang="ar" href={canonicalUrl} />
            <link {...hk('alternate-en')} rel="alternate" hrefLang="en" href={canonicalUrl} />
            <link {...hk('alternate-default')} rel="alternate" hrefLang="x-default" href={`${appUrl}/`} />

            {/* Open Graph — Facebook, WhatsApp, LinkedIn */}
            <meta {...hk('og:type')} property="og:type" content={type} />
            <meta {...hk('og:url')} property="og:url" content={canonicalUrl} />
            <meta {...hk('og:site_name')} property="og:site_name" content={SITE_NAME} />
            <meta {...hk('og:title')} property="og:title" content={title} />
            <meta {...hk('og:description')} property="og:description" content={description} />
            <meta {...hk('og:image')} property="og:image" content={imageUrl} />
            <meta {...hk('og:image:width')} property="og:image:width" content="1200" />
            <meta {...hk('og:image:height')} property="og:image:height" content="630" />
            <meta {...hk('og:image:type')} property="og:image:type" content="image/png" />
            <meta {...hk('og:image:alt')} property="og:image:alt" content={title} />
            <meta {...hk('og:locale')} property="og:locale" content={locale} />
            <meta {...hk('og:locale:alternate')} property="og:locale:alternate" content={altLocale} />

            {/* Twitter / X */}
            <meta {...hk('twitter:card')} name="twitter:card" content="summary_large_image" />
            <meta {...hk('twitter:url')} name="twitter:url" content={canonicalUrl} />
            <meta {...hk('twitter:title')} name="twitter:title" content={title} />
            <meta {...hk('twitter:description')} name="twitter:description" content={description} />
            <meta {...hk('twitter:image')} name="twitter:image" content={imageUrl} />
            <meta {...hk('twitter:image:alt')} name="twitter:image:alt" content={title} />
        </Head>
    );
}
